import { styled } from '@abyss/web/tools/styled';
import { Button } from '@abyss/web/ui/Button';
import { Card } from '@abyss/web/ui/Card';
import { IconBrand } from '@abyss/web/ui/IconBrand';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { Modal } from '@abyss/web/ui/Modal';
import find from 'lodash/find';
import React, { useContext } from 'react';

import { PortalContext } from '../../context/PortalContext';
import { useLagoon } from '../../hooks/useLagoon';
import { getLagoonConfigValue } from '../../utils/providerSearch.utils';
import { Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { getFeatureFlag } from '../Utils/getFeatureFlag';
import { PortalUrl } from "../../common/Portal";
import { getPortalConfigUrls } from '../../utils/portalConfig.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';

interface CrisisInterventionModalProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
}

const ModalContainer = styled(Modal, {
  '.abyss-modal-content-container': {
    width: '805px',
    height: 'auto',
  },
  '.abyss-modal-content-title': {
    fontFamily: 'var(--abyss-psx-parcel-fonts-primary) !important',
    fontWeight: '700',
    lineHeight: '40px',
  },
  '@media (max-width: 744px)': {
    '.abyss-modal-content-container': {
      width: '100%',
      position: 'fixed',
      bottom: '0',
      left: '0',
      right: '0',
      margin: '0',
      borderRadius: '4px 4px 0px 0px',
      boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
    },
    '.abyss-psx-parcel-c-PJLV-iWWDNo-isDrawer-false': {
      padding: '0px',
    },
    '.abyss-modal-content-title': {
      fontWeight: '700',
      fontSize: '28.83px',
      lineHeight: '36px',
    },
  },
});

const StyledLayoutGroup = styled(Layout.Group, {
  alignItems: 'stretch !important',
  display: 'flex',
  flexDirection: 'row',
  gap: '16px',
  width: '100%',
  '& >  *': {
    flex: '1',
    minWidth: '0',
  },
  '@media (max-width: 744px)': {
    flexDirection: 'column',
    '& >  *': {
      width: '100%',
    },
  },
});

const StyledCard = styled(Card, {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  flex: '1',
  minWidth: '0',
  borderRadius: '8px',
  border: '1px solid #CBCCCD',
  background: '#FFF',
  boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.05)',
});

const CardSectionTop = styled(Card.Section, {
  padding: '16px 16px 24px 16px !important',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  flex: 1,
});

const CardSectionBottom = styled(Card.Section, {
  padding: '16px !important',
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
});

const StyledText = styled('div', {
  color: '$orText',
  fontWeight: 600,
});

const IconWrapper = styled('div', {
  fontSize: '14px',
  display: 'flex',
  gap: '3px',
});

const Title = styled('div', {
  color: '$uhcPrimary1',
  fontSize: '20.25px',
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: '24px',
});

const CardSubtext = styled('div', {
  alignSelf: 'stretch',
  color: '#4B4D4F',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '18px',
});

const Subtext = styled('div', {
  color: '#323333',
  fontWeight: 500,
});

const StyledAnchor = styled('a', {
  color: '$subTextcolor',
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  '&:hover': {
    textDecoration: 'none',
  },
});

export const CrisisInterventionModal = React.forwardRef(
  ({ isOpen, setIsOpen }: CrisisInterventionModalProps, ref) => {
    const uiMessaging = useLagoon(Constants.LAGOON_TABLE.UI_MESSAGING)();
    const allConfigs = useLagoon('config');
    const featureFlags = useLagoon('feature-flags')();
    const { portalData } = useContext(PortalContext);

    const crisisModalCards = useLagoon(Constants.LAGOON_TABLE.CRISIS_MODAL)();

    const disableCrisisButton = portalData?.portalName
      ? getFeatureFlag(
        featureFlags,
        ConstantsLagoon.FEATURE_FLAGS[
        `DISABLE_CRISIS_KEYWORDS_CTA_${portalData.portalName}`
        ]
      )
      : getFeatureFlag(
        featureFlags,
        ConstantsLagoon.FEATURE_FLAGS.DISABLE_CRISIS_KEYWORDS_CTA
      );

    const crisisModalHeading = find(uiMessaging, {
      key: ConstantsLagoon.UI_MESSAGING.CRISIS_POPUP_HEADING,
    });

    const crisisModalSubtext = find(uiMessaging, {
      key: ConstantsLagoon.UI_MESSAGING.CRISIS_POPUP_SUBTEXT,
    });

    const viewCrisisResourcesButton = find(uiMessaging, {
      key: ConstantsLagoon.UI_MESSAGING
        .CRISIS_POPUP_VIEW_CRISIS_RESOURCES_BUTTON,
    });

    const closeButton = find(uiMessaging, {
      key: ConstantsLagoon.UI_MESSAGING.CLOSE,
    });

    const callInput = find(uiMessaging, {
      key: ConstantsLagoon.UI_MESSAGING.CALL,
    });

    const textInput = find(uiMessaging, {
      key: ConstantsLagoon.UI_MESSAGING.TEXT,
    });

    const orInput = find(uiMessaging, {
      key: ConstantsLagoon.UI_MESSAGING.OR,
    });

    const portalUrls: PortalUrl[] = getPortalConfigUrls();

    let crisisNationalResourcesURL: string | undefined;

    const portalKey = `CRISIS_NATIONAL_RESOURCES_URL_${portalData.portalName}`;
    const portalUrlObj = portalUrls.find(url => url.name === portalKey);

    if (portalUrlObj?.urlPath?.trim()) {
      // Always use portalUrls if the portal-specific key exists
      crisisNationalResourcesURL = `${window.location.origin}${portalUrlObj.urlPath}`;
    } else {
      // Try Lagoon config for the generic key first
      const lagoonUrl =
        getLagoonConfigValue(allConfigs, 'CRISIS_NATIONAL_RESOURCES_URL') ||
        getLagoonConfigValue(allConfigs, portalKey);

      crisisNationalResourcesURL = lagoonUrl?.trim() ? lagoonUrl : undefined;
    }

    return (
      <React.Fragment>
        <ModalContainer
          aria-describedby="crisis-modal-description"
          aria-labelledby="crisis-modal-title"
          data-testid="crisis-intervention-modal"
          footer={
            <Modal.Section data-testid="crisis-intervention-modal-buttons">
              <Layout.Group>
                {!disableCrisisButton && (
                  <Button
                    aria-label={viewCrisisResourcesButton?.message}
                    data-testid="crisis-button"
                    onClick={() => {
                      if (crisisNationalResourcesURL) {
                        adobeLinkTrackEvent({
                          name: viewCrisisResourcesButton?.message,
                          location: "search crisis intervention modal",
                          type: 'internal',
                        });
                        window.location.href = crisisNationalResourcesURL;
                      }
                    }}
                  >
                    {viewCrisisResourcesButton?.message}
                  </Button>
                )}
                <Button
                  aria-label="Close Crisis Intervention Modal"
                  data-testid="close-button"
                  onClick={() => setIsOpen(false)}
                  variant="outline"
                >
                  {closeButton?.message}
                </Button>
              </Layout.Group>
            </Modal.Section>
          }
          isOpen={isOpen}
          onClose={() => {
            setIsOpen(false);
          }}
          ref={ref}
          role="dialog"
          title={crisisModalHeading?.message}
        >
          <Modal.Section data-testid="crisis-intervention-modal-section">
            <Subtext>{crisisModalSubtext?.message}</Subtext>
          </Modal.Section>

          <Modal.Section>
            <StyledLayoutGroup data-testid="crisis-layout">
              {crisisModalCards.map((card, index) => (
                <StyledCard
                  aria-describedby={`card-subtext-${index}`}
                  aria-labelledby={`card-title-${index}`}
                  data-testid="crisis-cards"
                  key={card.id}
                >
                  <CardSectionTop data-testid="crisis-card-section-top">
                    <div className="icon">
                      {card.crisisIcon && (
                        <IconBrand icon={card.dntCrisisIcon} size="48px" />
                      )}
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gap: '8px',
                      }}
                    >
                      <Title id={`card-title-${index}`}>
                        {card.crisisTitle}
                      </Title>
                      <CardSubtext id={`card-subtext-${index}`}>
                        {card.crisisSubtitle}
                      </CardSubtext>
                    </div>
                  </CardSectionTop>
                  <CardSectionBottom data-testid="crisis-card-section-bottom">
                    <IconWrapper data-testid="crisis-card-icon-wrapper">
                      {index === 0 && (
                        <>
                          <StyledAnchor
                            aria-label={`Call ${card.callNumber}`}
                            data-testid={`call-link-${card.crisisIcon}`}
                            href={`tel:${card.callNumber}`}
                            onClick={() =>
                              adobeLinkTrackEvent({
                                name: `${callInput?.message} ${card.callNumber}`,
                                location: "search crisis intervention modal",
                                type: 'internal',
                              })
                            }
                          >
                            <IconSymbol
                              icon="call"
                              size="sm"
                              style={{ color: 'inherit' }}
                            />{' '}
                            {`${callInput?.message} ${card.callNumber}`}
                          </StyledAnchor>
                          <StyledText>{orInput?.message}</StyledText>
                          <StyledAnchor
                            aria-label={`Text ${card.callNumber}`}
                            data-testid={`text-link-${card.crisisIcon}`}
                            href={`sms:${card.callNumber}`}
                            onClick={() =>
                              adobeLinkTrackEvent({
                                name: `${textInput?.message} ${card.callNumber}`,
                                location: "search crisis intervention modal",
                                type: 'internal',
                              })
                            }
                          >
                            <IconSymbol
                              icon="textsms"
                              size="sm"
                              style={{ color: 'inherit' }}
                              variant="outlined"
                            />{' '}
                            {`${textInput?.message} ${card.callNumber}`}
                          </StyledAnchor>
                        </>
                      )}
                      {index === 1 && (
                        <>
                          <StyledAnchor
                            aria-label={`Call ${card.callNumber}`}
                            data-testid={`call-link-${card.crisisIcon}`}
                            href={`tel:${card.callNumber}`}
                            onClick={() =>
                              adobeLinkTrackEvent({
                                name: `${callInput?.message} ${card.callNumber}`,
                                location: "search crisis intervention modal",
                                type: 'internal',
                              })
                            }
                          >
                            <IconSymbol
                              icon="call"
                              size="sm"
                              style={{ color: 'inherit' }}
                            />{' '}
                            {`${callInput?.message} ${card.callNumber}`}
                          </StyledAnchor>
                        </>
                      )}
                      {index === 2 && (
                        <>
                          <StyledAnchor
                            aria-label={`Call ${card.callNumber}`}
                            data-testid={`call-link-${card.crisisIcon}`}
                            href={`tel:${card.callNumber}`}
                            onClick={() =>
                              adobeLinkTrackEvent({
                                name: `${callInput?.message} ${card.callNumber}`,
                                location: "search crisis intervention modal",
                                type: 'internal',
                              })
                            }
                          >
                            <IconSymbol
                              icon="call"
                              size="sm"
                              style={{ color: 'inherit' }}
                            />{' '}
                            {`${callInput?.message} ${card.callNumber}`}
                          </StyledAnchor>
                        </>
                      )}
                    </IconWrapper>
                  </CardSectionBottom>
                </StyledCard>
              ))}
            </StyledLayoutGroup>
          </Modal.Section>
        </ModalContainer>
      </React.Fragment>
    );
  }
);
